import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import Header from "@/components/Header";
import UrlInputHero from "@/components/UrlInputHero";
import FilterPanel from "@/components/FilterPanel";
import RentalCard from "@/components/RentalCard";
import RentalDetailModal from "@/components/RentalDetailModal";
import ManualAddDialog from "@/components/ManualAddDialog";
import BookmarkletDialog from "@/components/BookmarkletDialog";
import EmptyState from "@/components/EmptyState";
import {
  fetchRentals,
  fetchStats,
  toggleFavorite,
  deleteRental,
  updateRental,
} from "@/lib/api";
import { RENTAL } from "@/constants/testIds";

const defaultFilters = {
  min_rent: "",
  max_rent: "",
  min_bedrooms: "",
  min_bathrooms: "",
  min_sqft: "",
  city: "",
  garage: false,
  parking: false,
  pet_friendly: false,
  furnished: false,
  favorite: false,
};

export default function Dashboard() {
  const [rentals, setRentals] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [sort, setSort] = useState("recent");
  const [selected, setSelected] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [bookmarkletOpen, setBookmarkletOpen] = useState(false);
  const [bookmarkletTarget, setBookmarkletTarget] = useState(null);
  const [loading, setLoading] = useState(true);

  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([k, v]) => {
      if (typeof v === "boolean") return v;
      return v !== "" && v !== null && v !== undefined;
    });
  }, [filters]);

  const refresh = useCallback(async () => {
    try {
      const [list, s] = await Promise.all([
        fetchRentals({ ...filters, sort }),
        fetchStats(),
      ]);
      setRentals(list);
      setStats(s);
    } catch {
      toast.error("Could not load rentals");
    } finally {
      setLoading(false);
    }
  }, [filters, sort]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleFav = async (rental) => {
    try {
      const updated = await toggleFavorite(rental.id);
      setRentals((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
      if (selected?.id === updated.id) setSelected(updated);
      fetchStats().then(setStats).catch(() => {});
    } catch {
      toast.error("Could not toggle favorite");
    }
  };

  const handleUpdate = async (id, patch) => {
    const updated = await updateRental(id, patch);
    setRentals((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
    if (selected?.id === updated.id) setSelected(updated);
    return updated;
  };

  const handleDelete = async (rental) => {
    try {
      await deleteRental(rental.id);
      setRentals((rs) => rs.filter((r) => r.id !== rental.id));
      if (selected?.id === rental.id) setSelected(null);
      toast.success("Rental removed");
      fetchStats().then(setStats).catch(() => {});
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div>
      <Header
        stats={stats}
        onOpenBookmarklet={() => {
          setBookmarkletTarget(null);
          setBookmarkletOpen(true);
        }}
      />

      <UrlInputHero
        onAdded={() => refresh()}
        onOpenManual={() => setManualOpen(true)}
        onNeedsBookmarklet={({ url, host }) => {
          setBookmarkletTarget({ url, host });
          setBookmarkletOpen(true);
        }}
      />

      <main className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <FilterPanel
              filters={filters}
              setFilters={setFilters}
              sort={sort}
              setSort={setSort}
              onClear={() => setFilters(defaultFilters)}
            />
          </div>

          <div className="lg:col-span-9">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <p className="overline">Your Shortlist</p>
                <h2 className="font-heading text-3xl font-medium tracking-tight mt-1">
                  {rentals.length} {rentals.length === 1 ? "rental" : "rentals"}
                  {hasActiveFilters && (
                    <span className="text-stone-400"> · filtered</span>
                  )}
                </h2>
              </div>
            </div>

            {loading ? (
              <SkeletonGrid />
            ) : rentals.length === 0 ? (
              <EmptyState filtered={hasActiveFilters} />
            ) : (
              <div
                data-testid={RENTAL.rentalGrid}
                className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
              >
                {rentals.map((r, i) => (
                  <RentalCard
                    key={r.id}
                    rental={r}
                    index={i}
                    onOpen={setSelected}
                    onFav={handleFav}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <RentalDetailModal
        rental={selected}
        onClose={() => setSelected(null)}
        onFav={handleFav}
        onDelete={handleDelete}
        onUpdate={handleUpdate}
      />

      <ManualAddDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        onCreated={() => refresh()}
      />

      <BookmarkletDialog
        open={bookmarkletOpen}
        onOpenChange={(o) => {
          setBookmarkletOpen(o);
          if (!o) setBookmarkletTarget(null);
        }}
        targetUrl={bookmarkletTarget?.url}
        targetHost={bookmarkletTarget?.host}
      />
    </div>
  );
}

const SkeletonGrid = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <div
        key={i}
        className="bg-white rounded-3xl border border-stone-200 overflow-hidden animate-pulse"
      >
        <div className="aspect-[4/3] bg-stone-200" />
        <div className="p-5 space-y-3">
          <div className="h-7 bg-stone-200 rounded w-1/3" />
          <div className="h-4 bg-stone-200 rounded w-2/3" />
          <div className="h-4 bg-stone-200 rounded w-1/2" />
        </div>
      </div>
    ))}
  </div>
);
